import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@ncp/api-client";
import { useTranslation } from "@ncp/i18n";
import { useSave } from "@ncp/ui";

export type SolutionKind = "WebApp" | "Embedded" | "Channel" | "ExternalPortal";
export const SOLUTION_KINDS: SolutionKind[] = ["WebApp", "Embedded", "Channel", "ExternalPortal"];

export interface DeepLinkTemplate {
  key: string;
  label: string;
  labelAr?: string | null;
  /** Path relative to the base URL (`/cases/{caseId}`) or an absolute http(s) URL. */
  template: string;
  scope: string;
}

/** A deep-link scope and the placeholders its templates may use (`GET /api/platform/solutions/scopes`). */
export interface DeepLinkScope { key: string; label: string; placeholders: string[] }

/** State of the feature flag gating a surface; a flag that does not exist gates nothing. */
export interface SolutionFlag { key: string; exists: boolean; enabled: boolean; percentage: number; roleOverrides: number }

export interface Solution {
  id: string;
  key: string;
  name: string;
  nameAr?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  kind: SolutionKind;
  icon: string;
  baseUrl: string;
  deepLinks: DeepLinkTemplate[];
  requiredPermissions: string[];
  healthUrl?: string | null;
  flagKey?: string | null;
  flag?: SolutionFlag | null;
  supportsHandoff: boolean;
  adapterKey?: string | null;
  settings?: Record<string, unknown> | null;
  enabled: boolean;
  sortOrder: number;
  isBuiltIn: boolean;
  sourcePackageKey?: string | null;
  lastHealthAt?: string | null;
  lastHealthOk?: boolean | null;
  lastHealthLatencyMs?: number | null;
  /** What the health sweep calls: the health URL, else the base URL; channels without a health URL are not probed. */
  probeUrl?: string | null;
  usedByWorkspaceKeys: string[];
  threadsMonth: number;
  updatedAt: string;
}

/** Editable fields (what the editor holds and what `PUT` sends). */
export interface SolutionBody {
  name: string;
  nameAr?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  kind: SolutionKind;
  icon: string;
  baseUrl: string;
  deepLinks: DeepLinkTemplate[];
  requiredPermissions: string[];
  healthUrl?: string | null;
  flagKey?: string | null;
  supportsHandoff: boolean;
  adapterKey?: string | null;
  settings: Record<string, unknown> | null;
  enabled: boolean;
  sortOrder: number;
}

export interface SolutionProbe { key: string; ok: boolean; latencyMs: number; statusCode?: number | null; error?: string | null; url?: string | null; checkedAt: string }

export function solutionBodyOf(s: Solution): SolutionBody {
  return {
    name: s.name, nameAr: s.nameAr ?? null, description: s.description ?? null, descriptionAr: s.descriptionAr ?? null, kind: s.kind, icon: s.icon, baseUrl: s.baseUrl,
    deepLinks: s.deepLinks.map((l) => ({ key: l.key, label: l.label, labelAr: l.labelAr ?? null, template: l.template, scope: l.scope })),
    requiredPermissions: s.requiredPermissions ?? [], healthUrl: s.healthUrl ?? null, flagKey: s.flagKey ?? null, supportsHandoff: s.supportsHandoff,
    adapterKey: s.adapterKey ?? null, settings: s.settings ?? {}, enabled: s.enabled, sortOrder: s.sortOrder,
  };
}

const KEY = ["platform", "solutions"] as const;

export function useSolutions() {
  const api = useApi();
  return useQuery({ queryKey: KEY, queryFn: () => api.get<Solution[]>("/api/platform/solutions"), refetchInterval: 60_000 });
}

export function useSolution(key: string) {
  const api = useApi();
  return useQuery({ queryKey: [...KEY, key], queryFn: () => api.get<Solution>(`/api/platform/solutions/${encodeURIComponent(key)}`), enabled: !!key });
}

export function useDeepLinkScopes() {
  const api = useApi();
  return useQuery({ queryKey: [...KEY, "scopes"], queryFn: () => api.get<DeepLinkScope[]>("/api/platform/solutions/scopes"), staleTime: Infinity });
}

export function useSolutionActions() {
  const api = useApi();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: KEY });
    void qc.invalidateQueries({ queryKey: ["platform", "workspaces"] });
  };
  return {
    create: useSave({ mutationFn: (input: { key: string } & SolutionBody) => api.post<Solution>("/api/platform/solutions", input), onSuccess: invalidate }),
    update: useSave({ mutationFn: ({ key, body }: { key: string; body: SolutionBody }) => api.put<Solution>(`/api/platform/solutions/${encodeURIComponent(key)}`, body), onSuccess: invalidate }),
    remove: useSave({ mutationFn: (key: string) => api.delete<void>(`/api/platform/solutions/${encodeURIComponent(key)}`), onSuccess: invalidate }),
    // Reachability checks: a failed check is not headed "Not saved".
    probe: useSave({ mutationFn: (key: string) => api.post<SolutionProbe>(`/api/platform/solutions/${encodeURIComponent(key)}/probe`), failed: t("admin:common.error"), onSuccess: invalidate }),
    probeAll: useSave({ mutationFn: () => api.post<SolutionProbe[]>("/api/platform/solutions/probe"), failed: t("admin:common.error"), onSuccess: invalidate }),
  };
}

/** Example values for the template preview, per placeholder. */
export const SAMPLE_VALUES: Record<string, string> = {
  workspaceKey: "competition",
  caseId: "01a06877-ec91-762a-8617-bf6c6e565b2e",
  caseNumber: "EC-2026-000028",
  matterId: "01a0cb82-be17-7368-bfad-2ee4a0d5c2b1",
  documentId: "01a0c1d2-3e4f-7a5b-8c6d-7e8f9a0b1c2d",
  proposalId: "01a0b7c8-d9e0-7f1a-b2c3-d4e5f6a7b8c9",
  threadId: "01a0cb8b-78ae-7b2d-a439-c342750efd00",
  artifactId: "01a0c3e4-f5a6-7b7c-8d9e-0f1a2b3c4d5e",
  assistantKey: "competition-assistant",
};

export const PLACEHOLDER = /\{([a-zA-Z0-9_]+)\}/g;

/** Client-side mirror of the registry's template checks, so mistakes show while typing (the server re-checks on save). */
export function templateProblems(link: DeepLinkTemplate, scopes: DeepLinkScope[], others: DeepLinkTemplate[]): string[] {
  const problems: string[] = [];
  if (!/^[a-z][a-z0-9-]{1,63}$/.test(link.key)) problems.push("key");
  if (others.some((o) => o !== link && o.key.toLowerCase() === link.key.toLowerCase())) problems.push("duplicate");
  if (!link.label.trim()) problems.push("label");
  const scope = scopes.find((s) => s.key.toLowerCase() === link.scope.toLowerCase());
  if (!scope) problems.push("scope");
  if (!link.template.trim()) problems.push("template");
  else if (!link.template.startsWith("/") && !/^https?:\/\//i.test(link.template)) problems.push("relative");
  if (scope) for (const m of link.template.matchAll(PLACEHOLDER)) if (!scope.placeholders.includes(m[1])) problems.push(`placeholder:${m[1]}`);
  return problems;
}

export function previewUrl(baseUrl: string, template: string): string {
  const filled = template.replace(PLACEHOLDER, (_, p: string) => SAMPLE_VALUES[p] ?? `{${p}}`);
  return /^https?:\/\//i.test(filled) ? filled : `${baseUrl.replace(/\/$/, "")}${filled}`;
}
